import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper";
import apiClient from "../api/apiClient";
import { useAuth } from "../auth/AuthContext";

const durationLabel = (days) => {
  if (days === 30)  return "/ Month";
  if (days === 60)  return "/ 2 Months";
  if (days === 90)  return "/ 3 Months";
  if (days === 180) return "/ 6 Months";
  if (days === 365) return "/ Year";
  return `/ ${days} Days`;
};

// ─── Style constants ─────────────────────────────────────────────────────────
// White pill contrasts on both the default dark card (#222) and the
// primary-coloured active/hover state.
const libTagBase = {
  display: "inline-block",
  padding: "4px 13px",
  borderRadius: 3,
  background: "rgba(255,255,255,0.92)",
  color: "#111",
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: 0.3,
  lineHeight: "1.4",
  border: "none",
  outline: "none",
  cursor: "pointer",
};

const libSection = {
  borderTop: "1px solid rgba(255,255,255,0.15)",
  borderBottom: "1px solid rgba(255,255,255,0.15)",
  paddingTop: 14,
  paddingBottom: 16,
  marginBottom: 6,
};

const libLabel = {
  fontSize: 10,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: 1.8,
  color: "rgba(255,255,255,0.45)",
  marginBottom: 10,
};

// ─── PricingCard ─────────────────────────────────────────────────────────────

const PricingCard = ({ pkg, active, onMouseEnter, onGetAccess }) => {
  const navigate  = useNavigate();
  const libraries = pkg.libraries || [];

  const fallbackPoints = [
    ...libraries.map((l) => `Full ${l.name} Access`),
    "All Video Categories",
    "Stream on Any Device",
    `${pkg.duration_days} Days Validity`,
  ];

  const features = pkg.description_points?.length
    ? pkg.description_points
    : fallbackPoints;

  return (
    <div
      className={`pricingtable-wrapper box-hover style-1 ${active ? "active" : ""}`}
      onMouseEnter={onMouseEnter}
    >
      <div className="pricingtable-inner">

        {/* Package name tab — absolute top label */}
        <div className="pricingtable-title">{pkg.name}</div>

        {/* Price */}
        <div className="pricingtable-price">
          <h2 className="pricingtable-bx text-primary">
            {pkg.currency} {pkg.price}
            <small>{durationLabel(pkg.duration_days)}</small>
          </h2>
        </div>

        {/* ── Accessible libraries ── */}
        <div style={libSection}>
          <p style={libLabel}>Gives Access To</p>
          {libraries.length > 0 ? (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {libraries.map((lib) => (
                <button
                  key={lib.id}
                  style={libTagBase}
                  onClick={() => navigate(`/library/${lib.slug}`)}
                  title={`Browse ${lib.name}`}
                >
                  {lib.name}
                </button>
              ))}
            </div>
          ) : (
            <span style={{ color: "rgba(255,255,255,0.3)", fontSize: 13 }}>—</span>
          )}
        </div>

        {/* Features */}
        <ul className="pricingtable-features">
          {features.map((point, i) => (
            <li key={i}>{point}</li>
          ))}
        </ul>

        {/* CTA */}
        <div className="pricingtable-footer">
          <button className="btn btn-primary btn-skew" onClick={onGetAccess}>
            <span>Get Access</span>
          </button>
        </div>

      </div>
    </div>
  );
};

// ─── PricingTable ─────────────────────────────────────────────────────────────

const PricingTable = ({ showBackground = false, backgroundImage = null }) => {
  const [packages, setPackages]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [activeIdx, setActiveIdx] = useState(0);
  const { user } = useAuth();
  const navigate  = useNavigate();
  const prevRef   = useRef(null);
  const nextRef   = useRef(null);

  useEffect(() => {
    apiClient.get("/packages")
      .then((res) => setPackages(res.data.data || []))
      .catch(() => setPackages([]))
      .finally(() => setLoading(false));
  }, []);

  const handleGetAccess = (pkg) => {
    if (!user) {
      navigate("/auth");
      return;
    }
    const firstLib = pkg.libraries?.[0];
    navigate(firstLib ? `/library/${firstLib.slug}` : "/auth");
  };

  if (loading) {
    return (
      <section className="content-inner rounded-shape-top overflow-hidden bg-white">
        <div className="container text-center py-5">
          <p>Loading plans…</p>
        </div>
      </section>
    );
  }

  if (!packages.length) return null;

  const useSlider = packages.length > 3;

  return (
    <section
      className={`content-inner rounded-shape-top overflow-hidden ${showBackground ? "" : "bg-white"}`}
      style={showBackground && backgroundImage ? { backgroundImage: `url(${backgroundImage})` } : undefined}
    >
      <div className="container">

        {useSlider ? (
          <div style={{ position: "relative" }}>
            <button
              ref={prevRef}
              className="btn btn-primary btn-skew"
              style={{
                position: "absolute", left: "-20px", top: "50%",
                transform: "translateY(-50%)", zIndex: 10,
                padding: "8px 14px", lineHeight: 1,
              }}
            >
              <i className="fa fa-chevron-left" />
            </button>
            <button
              ref={nextRef}
              className="btn btn-primary btn-skew"
              style={{
                position: "absolute", right: "-20px", top: "50%",
                transform: "translateY(-50%)", zIndex: 10,
                padding: "8px 14px", lineHeight: 1,
              }}
            >
              <i className="fa fa-chevron-right" />
            </button>

            <Swiper
              modules={[Navigation]}
              slidesPerView={3}
              spaceBetween={24}
              navigation={{ prevEl: prevRef.current, nextEl: nextRef.current }}
              onBeforeInit={(swiper) => {
                swiper.params.navigation.prevEl = prevRef.current;
                swiper.params.navigation.nextEl = nextRef.current;
              }}
              onSlideChange={(swiper) => setActiveIdx(swiper.activeIndex)}
              breakpoints={{
                0:   { slidesPerView: 1 },
                768: { slidesPerView: 2 },
                992: { slidesPerView: 3 },
              }}
            >
              {packages.map((pkg, i) => (
                <SwiperSlide key={pkg.id}>
                  <PricingCard
                    pkg={pkg}
                    active={i === activeIdx}
                    onMouseEnter={() => setActiveIdx(i)}
                    onGetAccess={() => handleGetAccess(pkg)}
                  />
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        ) : (
          <div className="row">
            {packages.map((pkg, i) => (
              <div className="col-lg-4 col-md-6 m-b30" key={pkg.id}>
                <PricingCard
                  pkg={pkg}
                  active={i === activeIdx}
                  onMouseEnter={() => setActiveIdx(i)}
                  onGetAccess={() => handleGetAccess(pkg)}
                />
              </div>
            ))}
          </div>
        )}

      </div>
    </section>
  );
};

export default PricingTable;
